import { AppShell, Button, ConnectionStatus, RoleChip, StatusChip } from "@jod/ui";
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import {
  Banknote,
  CalendarClock,
  ClipboardList,
  Gift,
  LayoutGrid,
  Map as MapIcon,
  MonitorPlay,
  Settings,
  ShieldAlert,
  Table2,
  TriangleAlert,
} from "lucide-react";
import type { ReactNode } from "react";
import { useOpsAlerts } from "../features/ops/alerts.ts";
import { hasPermission } from "../features/ops/permissions.ts";
import { useStaffState } from "../features/ops/queries.ts";
import type { StaffPermission } from "../features/ops/types.ts";
import { getStaffPrincipal, signOutStaff } from "../lib/auth-session.ts";

export const Route = createFileRoute("/_app")({
  beforeLoad: () => {
    if (!getStaffPrincipal()) throw redirect({ to: "/login" });
  },
  component: AppLayout,
});

type AppPath =
  | "/today"
  | "/session-control"
  | "/orders"
  | "/gifts"
  | "/tables"
  | "/floor"
  | "/moderation"
  | "/cashier"
  | "/incidents"
  | "/screen-queue"
  | "/settings";

const NAV: {
  key: string;
  label: string;
  labelTh: string;
  icon: ReactNode;
  to: AppPath;
  permission?: StaffPermission;
}[] = [
  { key: "today", label: "Today", labelTh: "คืนนี้", icon: <LayoutGrid />, to: "/today" },
  {
    key: "session",
    label: "Session",
    labelTh: "รอบ",
    icon: <CalendarClock />,
    to: "/session-control",
    permission: "session:manage",
  },
  {
    key: "orders",
    label: "Orders",
    labelTh: "ออเดอร์",
    icon: <ClipboardList />,
    to: "/orders",
    permission: "order:view",
  },
  {
    key: "gifts",
    label: "Gifts",
    labelTh: "ของขวัญ",
    icon: <Gift />,
    to: "/gifts",
    permission: "gift:view",
  },
  {
    key: "tables",
    label: "Tables",
    labelTh: "โต๊ะ",
    icon: <Table2 />,
    to: "/tables",
    permission: "table:view",
  },
  {
    key: "floor",
    label: "Floor",
    labelTh: "ผังร้าน",
    icon: <MapIcon />,
    to: "/floor",
    permission: "table:view",
  },
  {
    key: "moderation",
    label: "Moderation",
    labelTh: "ความปลอดภัย",
    icon: <ShieldAlert />,
    to: "/moderation",
    permission: "moderation:view",
  },
  {
    key: "cashier",
    label: "Cashier",
    labelTh: "แคชเชียร์",
    icon: <Banknote />,
    to: "/cashier",
    permission: "cashier:view",
  },
  {
    key: "incidents",
    label: "Incidents",
    labelTh: "เหตุการณ์",
    icon: <TriangleAlert />,
    to: "/incidents",
    permission: "incident:view",
  },
  {
    key: "screen-queue",
    label: "Screen Queue",
    labelTh: "คิวขึ้นจอ",
    icon: <MonitorPlay />,
    to: "/screen-queue",
    permission: "screen:view",
  },
  { key: "settings", label: "Settings", labelTh: "ตั้งค่า", icon: <Settings />, to: "/settings" },
];

function AppLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const principal = getStaffPrincipal();
  const { data } = useStaffState();

  const visibleNav = NAV.filter(
    (item) => !item.permission || hasPermission(principal, item.permission),
  );
  const session = data?.session;
  // FR-STAFF-032/054/075/135: audible + visual alert for new pending orders,
  // accepted gifts, and high-severity reports. Muting audio never hides the
  // visual badge.
  const alerts = useOpsAlerts();

  const sidebar = (
    <>
      <div className="px-2 pb-4">
        <p className="font-display font-semibold text-lg tracking-tight">JOD Staff Ops</p>
        <p className="text-muted-foreground text-xs">Operational PWA · ไม่ใช่ LIFF</p>
      </div>
      <nav className="flex flex-col gap-1">
        {visibleNav.map((item) => {
          const active = pathname.startsWith(item.to);
          return (
            <Button
              key={item.key}
              variant={active ? "secondary" : "ghost"}
              className="min-h-11 w-full justify-start gap-3"
              onClick={() => navigate({ to: item.to })}
            >
              {item.icon}
              <span className="flex flex-col items-start leading-tight">
                <span>{item.label}</span>
                <span className="text-muted-foreground text-[11px]">{item.labelTh}</span>
              </span>
            </Button>
          );
        })}
      </nav>
    </>
  );

  const topbar = (
    <div className="flex w-full flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-display font-semibold text-sm">
          {data?.venue.name ?? "Just One Drink"}
        </span>
        <StatusChip
          tone={
            session?.status === "open"
              ? "success"
              : session?.status === "last_call"
                ? "warning"
                : "neutral"
          }
        >
          {session?.status ?? "loading"}
        </StatusChip>
        <ConnectionStatus degraded={data?.connection.degraded} offline={data?.connection.offline} />
        {session?.socialPaused ? <StatusChip tone="danger">social paused</StatusChip> : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {alerts.hasNewItems ? (
          <Button
            variant="destructive"
            size="sm"
            className="animate-pulse"
            onClick={alerts.acknowledge}
            title="New orders/gifts/reports since last check"
          >
            🔔 {alerts.total} new
          </Button>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          onClick={() => alerts.setEnabled(!alerts.enabled)}
          title="Muting sound never disables the visual alert (FR-STAFF-135)"
        >
          {alerts.enabled ? "🔔 On" : "🔕 Muted"}
        </Button>
        {principal?.roles.map((role) => (
          <RoleChip key={role} label={role} />
        ))}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            signOutStaff();
            void navigate({ to: "/login" });
          }}
        >
          Lock / logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <AppShell sidebar={sidebar} topbar={topbar}>
        <Outlet />
      </AppShell>
    </div>
  );
}
